import { Container, Modal } from '@cleartrip/ct-design-components'
import { getSrpRumContext, RUM_EVENT, trackRumEvent } from '@/analytics/sparkloom'

import dynamic from 'next/dynamic'
import SrpCouponsBar from '@/components/srp/SrpCouponsBar'
import { useRouter } from 'next/router'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactList from 'react-list'

import {
    addWowLoadingFareIds,
    getSrpSearchData,
    getWowFares,
    mergeWowFares,
    removeWowLoadingFareIds
} from '@/redux/slices/srpSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store/hooks'

import { RAVEN_EVENT_NAME } from '@/analytics/raven/ravenConstants'
import { generateSrpAdditionalPayload, ravenSDKSrpCfwTrigger } from '@/analytics/raven/ravenSDKHelper'
import Clevertap from '@/analytics/clevertap'
import { getWowPricingSrpData } from '@/api/srp'
import FlightDetails from '@/components/srp/FlightDetails'
import useWowPricingExperiment from '@/hooks/srp/useWowPricingExperiment'
import { isEmpty } from '@/utils/general/browserHelper'

import { getFormattedDate } from '@/utils/general/date'
import { isUserSignedIn } from '@/utils/general/user'
import {
    getFaresInfo,
    getFlightInfo,
    getSearchIntentDetails,
    getFarePricingAttributes,
    getCheapestFareInCard,
    getMasterTuppleProps,
    getLowestRoundTripPriceDetails,
    getSpecialRTDetails,
    getFFCookieValue,
    composeEventDescription2FromFare,
} from '@/utils/srp/helper'
import { getEliteBannerListIndex, isEliteFeatureEligible } from '@/utils/srp/loyaltySearchUtils'
import { ProcessedData } from '@/utils/srp/types'
import { collapseSamePriceCards } from '@/utils/srp/wow'
import { getFetchContentPayload } from '@/utils/srp/wowPayload'
import { Card, faresModalType, FareType, OfferType, QueryParamsData, showFlightDetailsType, SortOptionType } from '@/types/srp'

import HighRpsNew from '../HighRpsNew/HighRpsNew'
import OnewayTuppleLoader from '../Loaders/TuppleLoader/OnewayTuppleLoader'
import IntlOneWayTripTuppleLoader from '../Loaders/TuppleLoader/OnewayTuppleLoader/IntlOneWayTripTuppleLoader'
import OnewayTuppleLoaderFF from '../Loaders/TuppleLoader/OnewayTuppleLoader/OnewayTuppleLoaderFF'
import RoundTripTuppleLoader from '../Loaders/TuppleLoader/RoundTripTuppleLoader'
import FareFamilyModal from '@/components/srp/FareCards/FareFamilyModal'

import IntlRoundTripTuppleLoader from '../Loaders/TuppleLoader/RoundTripTuppleLoader/IntlRoundTripTuppleLoader'
import RoundTripTuppleLoaderFF from '../Loaders/TuppleLoader/RoundTripTuppleLoader/RoundTripTuppleLoaderFF'

import { getHighRpsDataFF, getPtcFilterDetails, isCheckInBaggageEnabled } from '@/utils/srp'
import useCreateItinerary from '@/utils/hooks/itinerary/useCreateItinerary'
import useVariant from '@/hooks/statsig/useVariant'
import EmiBlock from '@/components/itinerary/Aside/EmiBlock'
import { isFFFlowEnabled } from '@/utils/common'
import useMultiScrollDirection from '@/hooks/useMultiScrollDirection'

import SamePriceCardWrapper from '../Tupple/International/Oneway/SamePriceCardsSection/SamePriceCardWrapper'

import { MULTIPLE_AIRLINES_TXT, SELECTED_SAME_PRICE_CARD, SELECTED_SAME_PRICE_CARD_LIST } from '@/constants/Srp'
import useEmi from '@/hooks/srp/useEMI'



import VisaDenialSrpBanner from '../VisaDenialSrpBanner'
import VisaDenialSrpBannerRevamp from '../VisaDenialSrpBanner/Revamp'

import PtcError from '@/components/srp/Error/PtcError'
import SrpUnlockEliteBanner from '@/components/srp/SrpUnlockEliteBanner'
import useSrpRevampExperiment from '@/hooks/srp/useSrpRevampExperiment'
import { JOURNEY_LEG } from '@/components/srp/Tupple/Revamp/config'
import { SAME_PRICE_CARD_RADIUS } from '@/components/srp/Tupple/Revamp/config/tupleTheme'
import { toTupleOneWayViewModel, toTupleRoundTripViewModel } from '@/components/srp/Tupple/Revamp/lib/toCardViewModel'
import type { TupleRoundTripViewModel } from '@/components/srp/Tupple/Revamp/model/types'
import { TupleOneWay, TupleRoundTrip } from '@/components/srp/Tupple/Revamp'
import HighRpsGroup from '@/components/srp/Tupple/Revamp/ui/HighRpsGroup'
import SamePriceGroup from '@/components/srp/Tupple/Revamp/ui/SamePriceGroup'

const DomesticOnewayTupple = dynamic(
    () => import(/* webpackChunkName: "DomesticOnewayTupple" */ '../Tupple/Domestic/Oneway'),
    {
        loading: () => <OnewayTuppleLoader />
    }
)

const DomesticRoundTripTupple = dynamic(
    () => import(/* webpackChunkName: "DomesticRoundTripTupple" */ '../Tupple/Domestic/RoundTrip'),
    {
        loading: () => <RoundTripTuppleLoader />
    }
)

const DomesticOnewayTuppleFF = dynamic(
    () => import(/* webpackChunkName: "DomesticOnewayTupple" */ '../Tupple/Domestic/OnewayFF'),
    {
        loading: () => <OnewayTuppleLoader />
    }
)

const DomesticRoundTripTuppleFF = dynamic(
    () => import(/* webpackChunkName: "DomesticRoundTripTupple" */ '../Tupple/Domestic/RoundTripFF'),
    {
        loading: () => <RoundTripTuppleLoader />
    }
)

const InternationalRoundTripTupple = dynamic(
    () => import(/* webpackChunkName: "InternationalRoundTripTuppleFF" */ '../Tupple/International/RoundTrip'),
    {
        loading: () => <RoundTripTuppleLoader />
    }
)

const InternationalOnewayTupple = dynamic(
    () => import(/* webpackChunkName: "InternationalOnewayTuppleFF" */ '../Tupple/International/Oneway'),
    {
        loading: () => <OnewayTuppleLoader />
    }
)
const ResetFiltersBlock = dynamic(() => import(/* webpackChunkName: "ResetFiltersBlock" */ '../ResetFiltersBlock'), {
    loading: () => <></>
})

type TuppleRendererProps = {
    sortedFilteredCards: any
    isRT: boolean
    searchQuery: QueryParamsData
    selectedFlights: any
    setSelectedFlights: (selectedFlights: any[]) => void
    showPerAdultPricing: boolean
    isLoading: boolean
    resetFilters: () => void
    processedData: Partial<ProcessedData>
    selectedPrice:any
    selectedFilters:any
    fareType?: string
    initialTS: number
    isPTC: boolean
    activeSort: SortOptionType[]
    unModifiedSortedFilteredCards: any[]
    showEmi: boolean
    setShowEmi: (showEmi: boolean) => void
    pageScrollRef: React.RefObject<HTMLDivElement>
    updatedReturnListLength: number
    setUpdatedReturnListLength: (updatedReturnListLength: number) => void
    onCollapseStateChange?: (state: 'open' | 'collapsed') => void
    masterTuppleRef: React.RefObject<HTMLDivElement>
    setShowAnimation: (showAnimation: boolean) => void
    hasShownEmiTooltipRef: React.RefObject<boolean>
    toastHandler: (msg: string) => void
    filterData: any
    /**
     * Progressive WOW pricing — ref callback factory; attach the returned ref
     * to each card's outer container so the IntersectionObserver-based
     * sentinel can fire batches of 10 as the user scrolls (UIENG-6303).
     */
    makeWowCardRef?: (
        travelOptionId: string,
        cardId: string,
        index: number
    ) => (el: HTMLElement | null) => void
    isOnwardPtcFareAvailable: boolean
    isReturnPtcFareAvailable: boolean
    srpCouponList?: OfferType[]
    isSrpRevamp?: boolean
}
const TuppleRenderer: React.FC<TuppleRendererProps> = ({
    sortedFilteredCards,
    isRT,
    selectedFlights,
    setSelectedFlights,
    showPerAdultPricing,
    isLoading,
    resetFilters,
    filterData,
    searchQuery = {},
    processedData = {},
    selectedPrice,
    selectedFilters,
    fareType,
    initialTS,
    isPTC = false,
    activeSort = [],
    unModifiedSortedFilteredCards,
    showEmi,
    setShowEmi,
    pageScrollRef,
    updatedReturnListLength,
    setUpdatedReturnListLength,
    onCollapseStateChange,
    masterTuppleRef,
    setShowAnimation,
    hasShownEmiTooltipRef,
    toastHandler,
    makeWowCardRef,
    isOnwardPtcFareAvailable,
    isReturnPtcFareAvailable,
    srpCouponList = [],
    isSrpRevamp = false
}) => {
    const isIntl = searchQuery?.intl === 'y' 
    const listRef = useRef()
    const onwardScrollRef = useRef<HTMLDivElement>(null)
    const returnScrollRef = useRef<HTMLDivElement>(null)
    // Intl (incl. intl CFW) always uses fareType 'FF' → render the FF UI/flow.
    // Domestic CFW is the only non-FF flow.
    const isFFFlow = fareType === 'FF';
    const { variant: supercoinVariant } = useVariant({ experiment: 'supercoin_toggle', cookieValue: 'supercoinToggle', defaultVariant: 'default_off' }) || ""
    const [faresModalType, setFaresModal] = useState<faresModalType>({
        show: false,
        index: null
    })
    const [intlReturnCards, setIntlReturnCards] = useState(sortedFilteredCards?.[1] || [])
    const [cardDetails, setCardDetails] = useState<Card[]>([])
    const [flightDetails, setFlightDetails] = useState<showFlightDetailsType>({
        show: false,
        cardDetails: [],
        ptcFareType: ''
    })
    const { searchData, couponData, searchSubTravelOptions } = useAppSelector(getSrpSearchData)
    const { metaData, searchIntent, subTravelOptions, fares, highRpsTravelOptionIds, specialRTFareMap } = searchData
    /**
     * Source for the tuple strip's fares. `searchData.subTravelOptions` is rewritten by
     * `mergeInterstitialData` when the fare-family modal fetches — the interstitial returns
     * the full family ordered cheapest-first, which displaced the search response's lead
     * (branded) fare in the tuple as soon as the modal had been opened once. The snapshot
     * never changes after the search, so the strip stays put. Falls back to the live map if
     * the store was hydrated without going through `setSrpSearchData`.
     */
    const tupleSubTravelOptions = !isEmpty(searchSubTravelOptions) ? searchSubTravelOptions : subTravelOptions
    const highRpsRef = useRef(null)
    const { paxCriteria = [], J1from = '', J1to = '', J2from = '', J2to = '' } = getSearchIntentDetails(searchIntent)
    const rawOnwardCards = sortedFilteredCards?.[0] || []
    const showSamePriceGroups = isSrpRevamp && !isRT && isIntl
    const onwardCards = useMemo(() => {
        if (!showSamePriceGroups || !rawOnwardCards.length) return rawOnwardCards
        return collapseSamePriceCards(rawOnwardCards)
    }, [rawOnwardCards, showSamePriceGroups])
    const returnCards = sortedFilteredCards?.[1] || []
    const updatedReturnLeg = isIntl ? intlReturnCards : returnCards
    const createItinerary = useCreateItinerary()
    const [selectedSamePriceCardList, setSelectedSamePriceCardList] = useState<unknown[]>([])
    const [selectedSamePriceCard, setSelectedSamePriceCard] = useState<number | string | null>(null)
    const { ow: showCheckInBaggagePriceOW, rt: showCheckInBaggagePriceRT } = isCheckInBaggageEnabled(selectedFilters)

    // Same-price card WOW (intl OW only). The expanded same-price cards are
    // typically beyond the initial top-100 in `wowFares`, so trigger an
    // ad-hoc dynamic-content fetch with overrideCards covering exactly the
    // expanded list. Module-level dedup in api/srp/index.tsx ensures the
    // same payload coalesces if this re-fires.
    const dispatchSamePriceWow = useAppDispatch()
    const samePriceWowFiredRef = useRef<Set<string>>(new Set())
    const wowFaresCurrent = useAppSelector(getWowFares)
    const { wowPricingVariant: wowVariantForSamePrice, wowOn: wowOnForSamePrice } =
        useWowPricingExperiment(true)
    useEffect(() => {
        const list = (selectedSamePriceCardList || []) as any[]
        if (!isIntl || isRT) return
        if (!list.length) return
        if (!isUserSignedIn()) return
        if (!wowOnForSamePrice) return
        // Build the fetch list AND collect each card's lead fareId so we can
        // mark them as loading (drives the shimmer in WowFareCallout).
        const cardsToFetch: Array<{ cardId: string; travelOptionId: string }> = []
        const leadFareIdsForLoading: string[] = []
        list.forEach((c: any) => {
            const leadFareList = getFaresInfo(c, subTravelOptions, fares, false, showCheckInBaggagePriceOW) || []
            const leadFareId = leadFareList?.[0]?.fareId
            if (!leadFareId) return
            // Always queue lead fareId for shimmer feedback, even if cached
            // (avoids the brief flicker when the card was just fetched moments ago).
            if (!wowFaresCurrent?.[leadFareId]) {
                cardsToFetch.push({ cardId: c.cardId, travelOptionId: c.travelOptionId })
                leadFareIdsForLoading.push(leadFareId)
            }
        })
        if (!cardsToFetch.length) return
        const dedupKey = cardsToFetch.map(c => c.cardId).join(',')
        if (samePriceWowFiredRef.current.has(dedupKey)) return
        samePriceWowFiredRef.current.add(dedupKey)
        // Show shimmer on every same-price card that's about to be fetched.
        dispatchSamePriceWow(addWowLoadingFareIds(leadFareIdsForLoading))
        ;(async () => {
            try {
                const payload = getFetchContentPayload(
                    searchData,
                    {
                        isIntl: true,
                        tripType: 'oneway',
                        isCfw: !!processedData?.isCfw,
                        isInterstitial: false,
                        wowPricingVariant: wowVariantForSamePrice,
                        overrideCards: { J1: cardsToFetch }
                    }
                )
                const wowResponse = (await getWowPricingSrpData(
                    payload,
                    !!processedData?.isCfw
                )) as any
                const responseBody = wowResponse?.data ?? wowResponse ?? {}
                if (responseBody?.fares) {
                    dispatchSamePriceWow(mergeWowFares(responseBody.fares))
                }
            } catch (e) {
                console.error('Same-price WOW fetch failed:', e)
            } finally {
                // Clear shimmer regardless of success/fail so cards never get stuck.
                dispatchSamePriceWow(removeWowLoadingFareIds(leadFareIdsForLoading))
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSamePriceCardList])
    const { onwardFilterPtcType, returnFilterPtcType } = getPtcFilterDetails(selectedFilters)
    const router = useRouter()
    const specialFareType = processedData?.sft || ''
    const masterTuppleHeight = masterTuppleRef?.current?.clientHeight || 106
    const isIntlPtc = isIntl && !isEmpty(specialFareType)
    // Each leg scrolls in its own column inside the viewport-locked page: intl always, dom RT in the revamp.
    const hasLegScroll = isRT && (isIntl || isSrpRevamp)
    useMultiScrollDirection({
        scrollContainerRefs: hasLegScroll && !isLoading ? [onwardScrollRef, returnScrollRef] : [],
        threshold: 10,
        initialState: 'open',
        onStateChange: onCollapseStateChange
    })
    const { emiData = [],
        fetchEmiFailed
    } = useEmi({
        searchQuery: router?.query,
        sortedFilteredCards,
        reactListItemsOneWay: listRef,
        resultsData: searchData,
        isPerAdult: showPerAdultPricing,
        displayList: sortedFilteredCards?.[0] || [],
        showEmiPrice: showEmi,
        setShowEmiPrice: setShowEmi,
        pageScroll: pageScrollRef?.current,
        toastHandler
    })
    const totalRTPriceFilter = showPerAdultPricing ? selectedFilters?.totalRoundTripPrice?.currentADT : selectedFilters?.totalRoundTripPrice?.current

    const [isHighRpsExpanded, setIsHighRpsExpanded] = useState<boolean>(false)

    const onwardNormaliseContext = useMemo(() => {
        const searchedOrigin = (searchQuery?.from || J1from || '').toUpperCase()
        const searchedDestination = (searchQuery?.to || J1to || '').toUpperCase()
        let nearbyAirportCodes: Set<string> | undefined
        const nearbyFilter = filterData?.onwardLeg?.nearbyAirportFilter ?? []
        const codes = nearbyFilter.flatMap((n: any) => {
            const primary = (n.airportCode ?? '').toUpperCase()
            return (Array.isArray(n.selectedValues) ? n.selectedValues : []).filter(
                (v: any) => v && v.toUpperCase() !== primary
            )
        })
        if (codes.length > 0) {
            nearbyAirportCodes = new Set(codes)
        }
        return {
            subTravelOptions: searchData?.subTravelOptions ?? {},
            fares: searchData?.fares ?? {},
            metaData: searchData?.metaData ?? {},
            flights: searchData?.flights ?? {},
            showPerAdult: showPerAdultPricing,
            ptcFareType: onwardFilterPtcType,
            isIntl,
            nearbyAirportCodes,
            searchedOrigin,
            searchedDestination,
        }
    }, [searchData, showPerAdultPricing, searchQuery, J1from, J1to, filterData, onwardFilterPtcType, isIntl])

    const returnNormaliseContext = useMemo(() => {
        // Return leg origin is searched destination, return leg destination is searched origin
        const searchedOrigin = (searchQuery?.to || J1to || '').toUpperCase()
        const searchedDestination = (searchQuery?.from || J1from || '').toUpperCase()
        let nearbyAirportCodes: Set<string> | undefined
        const nearbyFilter = filterData?.returnLeg?.nearbyAirportFilter ?? []
        const codes = nearbyFilter.flatMap((n: any) => {
            const primary = (n.airportCode ?? '').toUpperCase()
            return (Array.isArray(n.selectedValues) ? n.selectedValues : []).filter(
                (v: any) => v && v.toUpperCase() !== primary
            )
        })
        if (codes.length > 0) {
            nearbyAirportCodes = new Set(codes)
        }
        return {
            subTravelOptions: searchData?.subTravelOptions ?? {},
            fares: searchData?.fares ?? {},
            metaData: searchData?.metaData ?? {},
            flights: searchData?.flights ?? {},
            showPerAdult: showPerAdultPricing,
            ptcFareType: returnFilterPtcType,
            isIntl,
            nearbyAirportCodes,
            searchedOrigin,
            searchedDestination,
        }
    }, [searchData, showPerAdultPricing, searchQuery, J1from, J1to, filterData, returnFilterPtcType, isIntl])

    useEffect(() => {
        if (!hasLegScroll && !isEmpty(listRef?.current?.items) && !(fareType === 'FF' && isPTC)) {
            try {
                listRef?.current?.items?.firstChild?.scrollIntoView({
                    block: 'end',
                    behavior: 'smooth'
                })
            } catch (error) {}
        } else if (hasLegScroll && onwardScrollRef?.current && returnScrollRef?.current) {
            onwardScrollRef.current.scrollTo({
                top: 0,
                behavior: 'smooth'
            })
            returnScrollRef.current.scrollTo({
                top: 0,
                behavior: 'smooth'
            })
        }
    }, [JSON.stringify(sortedFilteredCards)])

    useEffect(() => {
        const selectedFlightsArray = []
        if (!isEmpty(onwardCards)) {
            selectedFlightsArray[0] = onwardCards[0]
        }
        const updatedReturnList = updateReturnLeg(selectedFlightsArray[0]) || []
        if (!isEmpty(returnCards)) {
            selectedFlightsArray[1] = !isEmpty(updatedReturnList) ? updatedReturnList[0] : returnCards[0]
        }
        setUpdatedReturnListLength(updatedReturnList?.length)
        setSelectedFlights([...selectedFlightsArray])
    }, [JSON.stringify(sortedFilteredCards)])

    useEffect(() => {
        const updatedReturnList = updateReturnLeg(selectedFlights?.[0]) || []
        setUpdatedReturnListLength(updatedReturnList?.length)
    }, [selectedFlights?.[0]])

    const {highRpsCards = [], highRpsDiscountedPrice = 0} = useMemo(() => {
        if (fareType !== 'FF' || !highRpsTravelOptionIds || !sortedFilteredCards[0] || isIntl) return {}

        return getHighRpsDataFF(highRpsTravelOptionIds, sortedFilteredCards[0], fares, tupleSubTravelOptions, showPerAdultPricing)
    }, [highRpsTravelOptionIds, sortedFilteredCards, fareType, fares, tupleSubTravelOptions, showPerAdultPricing])

    const handleFlightSelection = (legNo: number, flight: Card) => {
        const updatedSelection = [...selectedFlights]
        updatedSelection[legNo] = flight
        setSelectedFlights(updatedSelection)
    }

    const onDomesticTuppleClick = (card: Card, index: number, type: 'continue' | 'fareTupple') => {
      setFaresModal({
        show: true,
        index,
      })
      setCardDetails([card])
        const ravenPayload = {
            action_name: 'a_srp_tupple_click',
            action_type: 'click',
            page_name: 'flights_srp',
            a_srp_ff_cookie: getFFCookieValue()
        }
      if (type === 'continue') {
        trackRumEvent(RUM_EVENT.SRP_BOOK_CLICKED, getSrpRumContext(processedData))
        sendFareModalRavenEvents(card, 'continue', index)
        ravenSDKSrpCfwTrigger('a_srp_tupple_click', searchData, processedData, { ...ravenPayload })
        if (isFFFlow) {
          sendFareModalRavenEvents(card, 'show', index)
        }
      } else {
        sendFareModalRavenEvents(card, 'show', index)
      }
    }

    const sortRtTotalPrice = (intlReturnCards = []) => {
        const returnList = [...intlReturnCards]
        const sortedList = returnList.sort((card1, card2) => {
            const totalPrice1 = card1?.totalPrice?.displayPrice || 0
            const totalPrice2 = card2?.totalPrice?.displayPrice || 0
            return totalPrice1 - totalPrice2
            
        })
        return sortedList
    }

    const getUpdatedReturnLegTotalPrice = (onwardSelectedFlight: unknown) => {
        let isSelectedReturnFlightAvailable = false
        const selectedReturnFlight = selectedFlights?.[1] || {}
        const updatedCardList = []
        returnCards.forEach((card = {}) => {
            const flights = [onwardSelectedFlight, card]
            const { totalFare = 0, totalStrikeOffPrice = 0, isSpecialRT, returnFareAvailable, onwardFareAvailable } =  getMasterTuppleProps({
                selectedFlights: flights,
                metaData,
                // Snapshot: these totals (and the availability filter below) decide the intl RT
                // return list, so they must track the same lead fare the tuple strip shows.
                subTravelOptions: tupleSubTravelOptions,
                fares,
                showPerAdultPricing,
                specialRTFareMap,
                isCheckInBaggageEnabledOw: showCheckInBaggagePriceOW,
                isCheckInBaggageEnabledRt: showCheckInBaggagePriceRT,
                onwardPtcFareType: onwardFilterPtcType,
                returnPtcFareType: returnFilterPtcType
            })
            if (!(onwardFareAvailable && returnFareAvailable) && !isSpecialRT) {
                return
            }
            if (typeof totalRTPriceFilter === "number" && totalFare > totalRTPriceFilter) {
                return
            }
            if (!isSelectedReturnFlightAvailable) {
                isSelectedReturnFlightAvailable = selectedReturnFlight?.travelOptionId === card?.travelOptionId
            }
            updatedCardList.push({
                ...card,
                isSpecialRT,
                totalPrice: {
                    displayPrice: totalFare,
                    strikeOffPrice: totalStrikeOffPrice
                }
            })
        })
        return { updatedCardList, isSelectedReturnFlightAvailable }
    }
    
    const updateReturnLeg = (onwardSelectedFlight: unknown) => {
        let finalList = returnCards
        if(isIntl) {
            if (!isEmpty(onwardSelectedFlight) && !isEmpty(returnCards)) {
                const { updatedCardList = [], isSelectedReturnFlightAvailable } = getUpdatedReturnLegTotalPrice(onwardSelectedFlight)
                finalList = updatedCardList
                if (activeSort?.[1]?.sortKey === 'total_price_up') {
                    finalList = sortRtTotalPrice(finalList);
                }
                if (!isSelectedReturnFlightAvailable) {
                    setSelectedFlights([selectedFlights?.[0], finalList?.[0]])
                }
                setIntlReturnCards(finalList)
            } else {
                setIntlReturnCards(returnCards)
            }
        }
        return finalList
    }

    const handleFlightDetails = (card: Card, legNo: number) => {
        const ptcFareType = legNo === 0 ? onwardFilterPtcType : returnFilterPtcType
        setFlightDetails({
            show: true,
            cardDetails: [card],
            ptcFareType
        })
        const triggerFlightDetailsRavenEvent = () => {
            const faresList = getFaresInfo(card, subTravelOptions, fares) || []
            const cheapestFare = Array.isArray(faresList) ? faresList.find((fare: any) => fare?.isCheapest) || faresList?.[0] : undefined
            const eventDescription2 = composeEventDescription2FromFare(cheapestFare, searchData)
            const payload = generateSrpAdditionalPayload({
                action_name: 'flight_details_click',
                action_type: 'click',
                property: 'srp_page' + (isRT ? '_rt' : '_ow'),
                listOfFares: faresList,
                index: card?.cardId,
                card: card,
                stops: 0,
                searchData: searchData,
                isSpecialRT: card?.isSpecialRT
            })
            ravenSDKSrpCfwTrigger('a_ui_action', searchData, processedData, { ...payload, event_description2: eventDescription2 })
        }

        triggerFlightDetailsRavenEvent()
    }

    const closeModal = () => {
        setFaresModal({
            show: false,
            index: null
        })
        sendFareModalRavenEvents(cardDetails?.[0], 'close', faresModalType?.index)
    }

    const sendFareModalRavenEvents = (
        card: Card,
        type: 'show' | 'close' | 'continue' = 'show',
        index: number | null = 0
    ) => {
        try {
            const { summary } = card || {}
            const { flights = [], stops = 0 } = summary || {}
            const flightNumberValue: string[] = []
            const airlineNamesValue: string[] = []
            flights?.map(flight => {
                flightNumberValue.push(`${flight?.airlineCode}-${flight?.flightNumber}`)
                airlineNamesValue.push(metaData?.airlineDetail?.[flight?.airlineCode]?.name)
            })
            const faresObjectList = getFaresInfo(card, subTravelOptions, fares, false, showCheckInBaggagePriceOW) || []
            const actionNameMap = {
                show: isFFFlow? 'ff_fare_sheet_open':'select_your_fare_card_open_click',
                close: isFFFlow? 'ff_fare_sheet_close_click': 'cfw_fare_selection_menu_close_click',
                continue: isFFFlow ? 'srp_flight_details_click':'flight_tuple_continue_click'
            }
            const ravenPayload = {
                action_name: actionNameMap?.[type],
                action_type: 'click',
                property: isFFFlow ?'book_button': 'srp_page',
                a_flight_tuple_num: index !== null ? index + 1 : 'N/A',
                a_flight_number: flightNumberValue?.join(',') || 'N/A',
                a_stop_count_onward_return: stops || 0,
                a_airline: airlineNamesValue?.join(',') || 'N/A',
                available_fares: faresObjectList?.length || 0,
                tuple_num: index !== null ? index + 1 : 'N/A',
                page_loadtime: new Date().getTime() - initialTS || 'N/A'

            }
            ravenSDKSrpCfwTrigger(RAVEN_EVENT_NAME.A_UI_ACTION, searchData, processedData, { ...ravenPayload })          
        }catch (error) {
            console.log(error)
        }
    }

    const getOwCardPrice = () => {
        if (!isEmpty(flightDetails.cardDetails) && flightDetails.show) {
            const { fares = {} } = searchData || {}
            // Snapshot, not the merged map — keeps this price on the same fare the tuple shows.
            let selectedFares = getCheapestFareInCard(flightDetails.cardDetails, tupleSubTravelOptions, fares, showCheckInBaggagePriceOW, onwardFilterPtcType) || []
            if (!isEmpty(selectedFares)) {
                const fareObj = getFarePricingAttributes({
                    fare: selectedFares?.[0],
                    showPerAdultPricing
                })
                return fareObj
            }
            return {}
        }
        return {}
    }

    const sendRavenEvents = ({legNo, card, faresObjectList, stops, index}: {legNo: number, card: Card, faresObjectList: FareType[], stops: number, index: number}) => {
        let flights = [], selectedFares = []
        const showCheckInBaggagePrice = legNo === 0 ? showCheckInBaggagePriceOW : showCheckInBaggagePriceRT
        if (legNo === 0) {
            const selectedFlightFares = getFaresInfo(selectedFlights?.[1], subTravelOptions, fares, false, showCheckInBaggagePrice, onwardFilterPtcType) || []
            flights = [card, selectedFlights?.[1]]
            selectedFares = [faresObjectList?.[0], selectedFlightFares?.[0]]
        } else {
            const selectedFlightFares = getFaresInfo(selectedFlights?.[0], subTravelOptions, fares, false, showCheckInBaggagePrice, returnFilterPtcType) || []
            flights = [selectedFlights?.[0], card]
            selectedFares = [selectedFlightFares?.[0], faresObjectList?.[0]]
        }
        const {isSpecialRT = false} = getSpecialRTDetails({
            selectedFares,
            selectedFlights,
            specialRTFareMap,
            fares,
            showPerAdultPricing,
            isCheckInBaggageEnabledOw: showCheckInBaggagePriceOW,
            isCheckInBaggageEnabledRt: showCheckInBaggagePriceRT,
            onwardPtcFareType: onwardFilterPtcType,
            returnPtcFareType: returnFilterPtcType
        })
        const payload = generateSrpAdditionalPayload({
            action_name: legNo === 0 ? 'srp_tuple_click' : 'srp_tuple_click_rt',
            action_type: 'click',
            property: 'srp_page' + (legNo === 0 ? '_ow' : '_rt'),
            listOfFares: faresObjectList,
            index,
            card,
            stops,
            searchData: searchData,
            isSpecialRT,
            legNo
        })
        ravenSDKSrpCfwTrigger('a_srp_tupple_click', searchData, processedData as Partial<ProcessedData>, { ...payload })
    }

    const handleSetState = ({ type, data }: { type: string, data: any }) => {
        switch (type) {
          case SELECTED_SAME_PRICE_CARD:
            setSelectedSamePriceCard(data)
            break
          case SELECTED_SAME_PRICE_CARD_LIST:
            setSelectedSamePriceCardList(data)
            break
        }
    }

    const getRtViewModel = useCallback(
        (card: Card, legNo: number): TupleRoundTripViewModel | null => {
            const leg = legNo === 0 ? JOURNEY_LEG.ONWARD : JOURNEY_LEG.RETURN
            const ctx = legNo === 0 ? onwardNormaliseContext : returnNormaliseContext
            const viewModel = toTupleRoundTripViewModel(card as any, ctx, leg)
            if (!viewModel) return null

            if (legNo === 1 && selectedFlights?.[0]) {
                const flights = [selectedFlights[0], card]
                const { totalFare = 0, totalStrikeOffPrice = 0, isSpecialRT } = getMasterTuppleProps({
                    selectedFlights: flights,
                    metaData,
                    subTravelOptions,
                    fares,
                    showPerAdultPricing,
                    specialRTFareMap,
                    isCheckInBaggageEnabledOw: showCheckInBaggagePriceOW,
                    isCheckInBaggageEnabledRt: showCheckInBaggagePriceRT,
                    onwardPtcFareType: onwardFilterPtcType,
                    returnPtcFareType: returnFilterPtcType,
                })
                viewModel.priceStrip = {
                    label: isSpecialRT ? 'Special return price' : 'Total price',
                    displayPrice: totalFare,
                    strikeOffPrice: totalStrikeOffPrice > totalFare ? totalStrikeOffPrice : undefined,
                }
            } else if (legNo === 1) {
                viewModel.priceStrip = {
                    label: 'Total price',
                }
            }

            return viewModel
        },
        [
            onwardNormaliseContext,
            returnNormaliseContext,
            selectedFlights,
            metaData,
            subTravelOptions,
            fares,
            showPerAdultPricing,
            specialRTFareMap,
            showCheckInBaggagePriceOW,
            showCheckInBaggagePriceRT,
            onwardFilterPtcType,
            returnFilterPtcType,
        ]
    )

    const resolveSiblingCards = (
        siblingIds: any[],
        parentCard: Card,
        cardsMap: Record<string, any> | undefined,
        fallbackPool: Card[]
    ): Card[] => {
        if (!Array.isArray(siblingIds) || siblingIds.length === 0) return []
        const seen = new Set<string>()
        const siblings: Card[] = []

        for (const idOrCard of siblingIds) {
            // Skip missing/blank ids (undefined, null, '', NaN, {}) but keep cardId 0 — the first card in every response.
            const isMissingId =
                idOrCard == null ||
                idOrCard === '' ||
                Number.isNaN(idOrCard) ||
                (typeof idOrCard === 'object' && isEmpty(idOrCard))
            if (isMissingId) continue
            
            let match: Card | null = null

            if (typeof idOrCard === 'object' && idOrCard !== null) {
                const cId = (idOrCard as any).cardId
                const toId = (idOrCard as any).travelOptionId
                if (cardsMap && typeof cardsMap === 'object' && !Array.isArray(cardsMap)) {
                    if ((cId != null && (cardsMap[cId] || cardsMap[String(cId)])) || (toId && cardsMap[toId])) {
                        match = idOrCard
                    }
                } else {
                    match = idOrCard
                }
            } else {
                if (cardsMap && typeof cardsMap === 'object' && !Array.isArray(cardsMap)) {
                    match = cardsMap[idOrCard] ?? cardsMap[String(idOrCard)] ?? null
                } else if (Array.isArray(fallbackPool)) {
                    const idStr = String(idOrCard)
                    match =
                        fallbackPool.find(
                            (c: any) =>
                                (c?.cardId != null && (c.cardId == idOrCard || String(c.cardId) === idStr)) ||
                                (c?.travelOptionId && (c.travelOptionId === idOrCard || c.travelOptionId === idStr))
                        ) || null
                }
            }

            if (
                match &&
                match?.cardId !== parentCard?.cardId &&
                match?.travelOptionId !== parentCard?.travelOptionId
            ) {
                const key = String(match?.travelOptionId ?? match?.cardId)
                if (!seen.has(key)) {
                    seen.add(key)
                    siblings.push(match)
                }
            }
        }
        return siblings
    }

    const renderOwRevampCard = useCallback(
        (card: Card, index: number, isHighRps?: boolean, type: any = 'normal') => {
            const viewModel = toTupleOneWayViewModel(card as any, onwardNormaliseContext)
            if (!viewModel) return null
            const bookClickType: 'continue' | 'fareTupple' = isIntl ? 'fareTupple' : 'continue'
            const cardKey = card?.cardId ?? card?.travelOptionId
            const shouldShowSamePrice = showSamePriceGroups && !isHighRps && type === 'normal'
            const siblingIds = card.samePriceCards || []
            const cardsMap = unModifiedSortedFilteredCards?.[0]
            const resolvedSiblings = shouldShowSamePrice
                ? resolveSiblingCards(siblingIds, card, cardsMap, rawOnwardCards)
                : []
            const count = resolvedSiblings.length
            const isExpanded =
                selectedSamePriceCard != null &&
                (String(selectedSamePriceCard) === String(cardKey) ||
                    (card?.travelOptionId != null && String(selectedSamePriceCard) === String(card.travelOptionId)))

            if (count > 0) {
                const activeSiblings = isExpanded
                    ? Array.isArray(selectedSamePriceCardList) && selectedSamePriceCardList.length > 0
                        ? (selectedSamePriceCardList as Card[])
                        : resolvedSiblings
                    : []

                return (
                    <Container className="mb-3">
                        <SamePriceGroup
                            count={count}
                            expanded={isExpanded}
                            siblings={
                                isExpanded
                                    ? activeSiblings.map((siblingCard: any, sIdx: number) => {
                                          const sViewModel = toTupleOneWayViewModel(siblingCard, onwardNormaliseContext)
                                          if (!sViewModel) return null
                                          return (
                                              <TupleOneWay
                                                  key={siblingCard.travelOptionId || siblingCard.cardId || sIdx}
                                                  cabin={searchQuery?.class}
                                                  card={sViewModel}
                                                  cardIndex={index}
                                                  paxCriteria={paxCriteria}
                                                  onBook={() => onDomesticTuppleClick(siblingCard, index, bookClickType)}
                                                  onFlightDetails={() => handleFlightDetails(siblingCard, 0)}
                                                  radius={SAME_PRICE_CARD_RADIUS}
                                              />
                                          )
                                      }).filter(Boolean)
                                    : null
                            }
                            onCollapse={() => {
                                setSelectedSamePriceCard(null)
                                setSelectedSamePriceCardList([])
                            }}
                            onExpand={() => {
                                setSelectedSamePriceCard(cardKey)
                                setSelectedSamePriceCardList(resolvedSiblings)
                                const airlineName =
                                    viewModel.airlines
                                        ?.map((a: any) => a.name)
                                        .filter(Boolean)
                                        .join(',') || 'N/A'
                                const flightNumber =
                                    viewModel.airlines
                                        ?.map((a: any) => `${a.code}-${a.flightNumber}`)
                                        .filter(Boolean)
                                        .join(',') || 'N/A'
                                const farePrice = viewModel.price?.displayPrice || 0
                                const additionalPayload = {
                                    flights_count: count,
                                    page_name: 'flights_srp',
                                    action_name: 'more_option_at_same_price_click',
                                    action_type: 'click',
                                    a_flight_tuple_num: index !== null ? index + 1 : 'N/A',
                                    property: 'flight_tuple_onward',
                                    a_airline: airlineName,
                                    a_flight_number: flightNumber,
                                    a_fare_price: farePrice,
                                }
                                ravenSDKSrpCfwTrigger('a_ui_action', searchData, processedData, { ...additionalPayload })
                            }}
                        >
                            <TupleOneWay
                                cabin={searchQuery?.class}
                                card={viewModel}
                                cardIndex={index}
                                paxCriteria={paxCriteria}
                                onBook={() => onDomesticTuppleClick(card, index, bookClickType)}
                                onFlightDetails={() => handleFlightDetails(card, 0)}
                                radius={SAME_PRICE_CARD_RADIUS}
                            />
                        </SamePriceGroup>
                    </Container>
                )
            }

            return (
                <Container className={isHighRps ? undefined : "mb-3"}>
                    <TupleOneWay
                        cabin={searchQuery?.class}
                        card={viewModel}
                        cardIndex={index}
                        paxCriteria={paxCriteria}
                        onBook={() => onDomesticTuppleClick(card, index, bookClickType)}
                        onFlightDetails={() => handleFlightDetails(card, 0)}
                    />
                </Container>
            )
        },
        [
            onwardNormaliseContext,
            showSamePriceGroups,
            selectedSamePriceCard,
            selectedSamePriceCardList,
            searchQuery?.class,
            paxCriteria,
            onDomesticTuppleClick,
            handleFlightDetails,
            unModifiedSortedFilteredCards,
            rawOnwardCards,
            searchData,
            processedData,
            isIntl,
        ]
    )

    const renderFlightCard = (cards: Card[], index: number, key: React.Key | null | undefined, legNo: number, isHighRps?: boolean, type?: ListType = "normal") => {
        let card: Card = cards?.[index] || {}
        if (isEmpty(card)) return ''

        if (type === "samePrice") {
            card = selectedSamePriceCardList[index] || {}
        }
        let emiPrice = {} 
        if (showEmi) {
            if (type === "samePrice") {
                emiPrice = emiData[selectedSamePriceCard] || {}
            } else {
                emiPrice = emiData[card.cardId] || {}
            }
        }
        const {
            flightNumber,
            airlineCode,
            airlineName,
            isMultiAirline,
            departureTime,
            arrivalTime,
            stops,
            timelineText,
            totalDuration,
            from,
            to,
            travelOptionId,
            departureAirportCode,
            arrivalAirportCode,
            subTravelOptionId,
            lowestRoundTripPriceDetails,
            dayDiff,
            layoverInfo
        } = getFlightInfo(card, metaData) || {}

        const airlineDisplayName = isMultiAirline ? MULTIPLE_AIRLINES_TXT : airlineName
        const formattedDepartureTime = getFormattedDate(departureTime, 'HH:mm', 'YYYY-MM-DDTHH:mm:ss')
        const formattedArrivalTime = getFormattedDate(arrivalTime, 'HH:mm', 'YYYY-MM-DDTHH:mm:ss')
        const flightDuration = `${totalDuration.hh ? `${totalDuration.hh}h` : ''} ${
            totalDuration.mm ? `${totalDuration.mm}m` : ''
        }`
        const ptcFareType = legNo === 0 ? onwardFilterPtcType : returnFilterPtcType
        const showCheckInBaggagePrice = legNo === 0 ? showCheckInBaggagePriceOW : showCheckInBaggagePriceRT
        const faresObjectList: FareType[] = getFaresInfo(card, tupleSubTravelOptions, fares, false, showCheckInBaggagePrice, ptcFareType) || []
        const faresToDisplayOnSRP = faresObjectList.slice(0, 2)
        const moreFaresCount = faresObjectList.length - 2

        const isSelected = travelOptionId == selectedFlights[legNo]?.travelOptionId
        const { totalFareCount = 0 ,showInterstitial=false} = tupleSubTravelOptions?.[subTravelOptionId] || {}
        const strikeOffDetails = getLowestRoundTripPriceDetails(lowestRoundTripPriceDetails, showPerAdultPricing)

        const commonProps = {
            flightNumber,
            airlineCode,
            airlineName: airlineDisplayName,
            isMultiAirline,
            departureTime: formattedDepartureTime,
            arrivalTime: formattedArrivalTime,
            arrivalTimeFromSearch: arrivalTime,
            stops,
            timelineText,
            layover: layoverInfo,
            flightDuration,
            from,
            to,
            listOfFares: faresToDisplayOnSRP,
            moreFaresCount,
            key,
            paxCriteria,
            showPerAdultPricing,
            index,
            faresObjectList,
            selectedPrice,
            selectedFilters,
            departureAirportCode,
            arrivalAirportCode,
            legNo,
            totalFareCount,
            showInterstitial,
            dayDiff,
            // WOW pricing — used by per-fare WowFareCallout to suppress
            // tooltip on high-RPS cards and gate domestic vs intl behaviour.
            travelOptionId,
            isIntl
        }
        const onTuppleClick = () => {
            if (!isSelected) {
                setShowAnimation(false)
                handleFlightSelection(legNo, card)
                sendRavenEvents({legNo, card, faresObjectList, stops, index})
            }
        }

        const canShowNAFlight = isIntl ? legNo === 0 || (legNo === 1 && card?.isSpecialRT) : false
        if (isEmpty(faresToDisplayOnSRP) && !canShowNAFlight) {
            return <></>
        }
        // Progressive WOW — only attach the sentinel ref on the OW leg
        // (legNo 0), only when the parent supplied the factory, and NEVER on
        // high-RPS cards (those are rendered in their own ReactList and would
        // collide with the main list's index map).
        const wowRef =
            legNo === 0 && makeWowCardRef && !isHighRps
                ? makeWowCardRef(travelOptionId, card.cardId, index)
                : undefined

        return (
            <div
                ref={wowRef as any}
                data-testid="flight-card"
                data-leg={legNo}
                data-card-index={index}
                data-travel-option-id={travelOptionId}
                data-selected={String(isSelected)}
            >
                {isSrpRevamp ? (
                    isRT ? (
                        (() => {
                            const rtViewModel = getRtViewModel(card, legNo)
                            if (!rtViewModel) return null
                            return (
                                <Container className="mb-3">
                                    <TupleRoundTrip
                                        cabin={searchQuery?.class}
                                        card={rtViewModel}
                                        cardIndex={index}
                                        isSelected={isSelected}
                                        leg={legNo === 0 ? JOURNEY_LEG.ONWARD : JOURNEY_LEG.RETURN}
                                        onFlightDetails={() => handleFlightDetails(card, legNo)}
                                        onSelect={onTuppleClick}
                                        onStripClick={onTuppleClick}
                                    />
                                </Container>
                            )
                        })()
                    ) : (
                        renderOwRevampCard(card, index, isHighRps, type)
                    )
                ) : (
                    <>
                {fareType === 'CFW' && (isRT ? (
                    <DomesticRoundTripTupple {...commonProps} isSelected={isSelected} onTuppleClick={onTuppleClick} />
                ) : (
                    <DomesticOnewayTupple
                        {...commonProps}

                        onContinueClick={(type = 'fareTupple') => onDomesticTuppleClick(card, index, type)}
                        onFlightDetailsClick={() => handleFlightDetails(card, 0)}
                    />
                ))}
                {fareType === 'FF' && (isRT ? (
                    <>
                        {isIntl ? (
                            <InternationalRoundTripTupple {...commonProps} isSelected={isSelected} onTuppleClick={onTuppleClick} strikeOffDetails={strikeOffDetails} card={card} onFlightDetailsClick={() => handleFlightDetails(card, legNo)}/> 
                        ) : (
                            <DomesticRoundTripTuppleFF {...commonProps} isSelected={isSelected} onTuppleClick={onTuppleClick} />
                        )}
                    </>
                ) : (
                    <>
                    {isIntl ? (
                        <SamePriceCardWrapper
                        processedData={processedData}
                        card={card}
                        selectedSamePriceCard={selectedSamePriceCard}
                        setSelectedSamePriceCard={setSelectedSamePriceCard}
                        renderSingleJourneyCards={renderFlightCard}
                        setSelectedSamePriceCardList={setSelectedSamePriceCardList}
                        selectedSamePriceCardList={selectedSamePriceCardList}
                        type={type}
                        isCfw={!!processedData?.isCfw}
                      >
                        <InternationalOnewayTupple showEmi={showEmi} emiPrice={emiPrice} {...commonProps} isSelected={isSelected} onFlightDetailsClick={() => handleFlightDetails(card, 0)} onTuppleClick={onTuppleClick} selectedSamePriceCard={selectedSamePriceCard} handleSetState={handleSetState} listType={type} card={card} unModifiedSortedFilteredCards={unModifiedSortedFilteredCards} hasShownEmiTooltipRef={hasShownEmiTooltipRef} searchData={searchData} processedData={processedData} onContinueClick={() => onDomesticTuppleClick(card, index, type)}/>
                      </SamePriceCardWrapper>
                    ):(
                        <DomesticOnewayTuppleFF {...commonProps}
                        onContinueClick={(type = 'fareTupple') => onDomesticTuppleClick(card, index, type)}
                        onFlightDetailsClick={() => handleFlightDetails(card, 0)}
                    />
                    )}
                    </>
                ))}
                    </>
                )}
            </div>
        )
    }

    // Revamp moves the one-way coupon strip off the top of the page into the list.
    const showRevampCouponStrip = isSrpRevamp && !isRT && !isEmpty(srpCouponList)
    const revampCouponStrip = (
        <Container className="my-2">
            <SrpCouponsBar isRevamp srpCouponList={srpCouponList} />
        </Container>
    )

    const renderFlightCardWithBanner = (cards: Card[], index: number, key: React.Key | null | undefined, legNo: number) => {
        const cardNode = renderFlightCard(cards, index, key, legNo)
        // Coupons go above a lone tuple, else between tuples 1–2 / 2–3 / 3–4 for 2 / 3 / 4+ results.
        const showCouponStrip = showRevampCouponStrip && index === Math.min(cards.length, 4) - 1

        let bannerNode: React.ReactNode = null
        if (filterData?.onwardLeg?.visaRejection && !isRT && index === 2) {
            bannerNode = isSrpRevamp ? (
                <Container className='mb-3'>
                    <VisaDenialSrpBannerRevamp />
                </Container>
            ) : (
                <Container style={{ marginTop: '-4px' }}>
                    <VisaDenialSrpBanner />
                </Container>
            )
        } else if (!isRT && index === getEliteBannerListIndex(processedData) && isEliteFeatureEligible(processedData)) {
            // Elite loyalty banner: after 2nd tuple on CFW OW, after 3rd tuple on domestic FF OW
            bannerNode = <SrpUnlockEliteBanner processedData={processedData} isSearchLoading={isLoading} />
        }

        return (
            <Fragment key={key}>
                {showCouponStrip && revampCouponStrip}
                {bannerNode}
                {cardNode}
            </Fragment>
        )
    }

    if (isLoading) {
        if(isIntl){
            return  isRT ?  <IntlRoundTripTuppleLoader/> : <IntlOneWayTripTuppleLoader />
        }
        if (isRT) {
            return fareType === 'FF' ? <RoundTripTuppleLoaderFF/> :  <RoundTripTuppleLoader />
        }
        return fareType === 'FF' ?  <OnewayTuppleLoaderFF/> : <OnewayTuppleLoader />
    }
    const hasHighRps = Boolean(highRpsCards?.length && !isIntl)

    // Revamp: the booking bar sits in the results column's flow below the legs, so they need no clearance for it.
    const rtListMarginBottom = isSrpRevamp ? '0px' : `${masterTuppleHeight + 60}px`
    const listMarginTop = isSrpRevamp ? (hasHighRps ? 'mt-0' : 'mt-4') : 'mt-6'
    const rtListClassName = hasLegScroll ? 'p-absolute t-0 b-0 w-100p' : ''
    const legScrollClassName = hasLegScroll ? 'oy-scroll no-scrollbar pb-4' : ''

    return (
        <>
                {
                    hasHighRps ? (
                        isSrpRevamp ? (
                            <Container className="mt-4 mb-3">
                                <HighRpsGroup
                                    expanded={isHighRpsExpanded}
                                    extraCount={highRpsCards.length - 1}
                                    siblings={highRpsCards.slice(1).map((_c: any, ind: number) => (
                                        <div key={_c.travelOptionId || ind}>
                                            {renderFlightCard(highRpsCards, ind + 1, `high-rps-${ind + 1}`, 0, true)}
                                        </div>
                                    ))}
                                    startingPrice={highRpsDiscountedPrice}
                                    onCollapse={() => {
                                        setIsHighRpsExpanded(false)
                                        Clevertap.event('Air_UI_Action', {
                                            action_name: 'high_rps_flights_section_expanded',
                                            action_value: 'hided',
                                            high_rps_flights_count: highRpsCards.length
                                        })
                                    }}
                                    onExpand={() => {
                                        setIsHighRpsExpanded(true)
                                        Clevertap.event('Air_UI_Action', {
                                            action_name: 'high_rps_flights_section_expanded',
                                            action_value: 'expanded',
                                            high_rps_flights_count: highRpsCards.length
                                        })
                                    }}
                                >
                                    {renderFlightCard(highRpsCards, 0, 'high-rps-0', 0, true)}
                                </HighRpsGroup>
                            </Container>
                        ) : (
                            <HighRpsNew
                            count={highRpsCards.length}
                            highRpsDiscountedPrice={highRpsDiscountedPrice}
                            isPerAdult={showPerAdultPricing}
                        >
                            <ReactList
                                threshold={200}
                                itemRenderer={(ind: number, key: any) => renderFlightCard(onwardCards, ind, key, 0, true)}
                                length={highRpsCards.length}
                                ref={highRpsRef}
                                // key={tupleKeys[0]}
                            />
                        </HighRpsNew>
                        )
                    ) : null
                }
                {isRT ? (
                    <Container className='p-relative flex-1 w-100p'>
                    <Container className={`flex-1 flex ${listMarginTop} ${rtListClassName}`} style={{ marginBottom: rtListMarginBottom }}>
                        <Container className="flex flex-column w-50p">
                            {isIntlPtc && (
                                <PtcError showPtcError={!isOnwardPtcFareAvailable && isReturnPtcFareAvailable} className="mb-3" cards={onwardCards} />
                            )}
                            <Container className={`pr-2 ${legScrollClassName}`} ref={onwardScrollRef}>
                                {onwardCards.length ? (
                                    <ReactList
                                        threshold={200}
                                        itemRenderer={(index: number, key: React.Key | null | undefined) =>
                                            renderFlightCard(onwardCards, index, key, 0)
                                        }
                                        length={onwardCards.length}
                                        ref={listRef}
                                    />
                                ) : (
                                    <ResetFiltersBlock from={J1from} to={J1to} onButtonClick={resetFilters} isRT={true} hasLegScroll={hasLegScroll} masterTuppleHeight={masterTuppleHeight}/>
                                )}
                            </Container>
                        </Container>
                        <Container className="flex flex-column w-50p">
                            {isIntlPtc && (
                                <Container className='pl-2'>
                                  <PtcError showPtcError={!isReturnPtcFareAvailable && isOnwardPtcFareAvailable} className="mb-3" cards={updatedReturnLeg} />
                                </Container>  
                            )}
                            <Container className={`pl-2 ${legScrollClassName}`} ref={returnScrollRef}>
                                {updatedReturnLeg.length ? (
                                    <ReactList
                                        threshold={200}
                                        itemRenderer={(index: number, key: React.Key | null | undefined) => {
                                            return renderFlightCard(updatedReturnLeg, index, key, 1)
                                        }}
                                        length={updatedReturnLeg.length}
                                        ref={listRef}
                                    />
                                ) : (
                                    <ResetFiltersBlock from={J2from} to={J2to} onButtonClick={resetFilters} isRT={true} hasLegScroll={hasLegScroll} masterTuppleHeight={masterTuppleHeight}/>
                                )}
                            </Container>
                        </Container>
                    </Container>
                    </Container>
                ) : (
                    <>
                        {isIntlPtc && (
                            <PtcError showPtcError={!isOnwardPtcFareAvailable} className="mt-10" cards={onwardCards} />
                        )}
                        <Container className={listMarginTop}>
                            {isEmpty(onwardCards) ? (
                                <>
                                    {showRevampCouponStrip && revampCouponStrip}
                                    <Container className="pt-9">
                                        <ResetFiltersBlock from={J1from} to={J1to} onButtonClick={resetFilters} isRT={false} />
                                    </Container>
                                </>
                            ) : (
                                <ReactList
                                    threshold={200}
                                    itemRenderer={(index: number, key: React.Key | null | undefined) =>
                                        renderFlightCardWithBanner(onwardCards, index, key, 0)
                                    }
                                    length={onwardCards.length || 0}
                                    ref={listRef}
                                />
                            )}
                        </Container>
                    </>
                )}
                <FlightDetails
                    searchQuery={searchQuery}
                    flightDetails={flightDetails}
                    setFlightDetails={setFlightDetails}
                    showPerAdultPricing={showPerAdultPricing}
                    onDomesticTuppleClick={onDomesticTuppleClick}
                    pricingDetails={getOwCardPrice()}
                    isCheckInBaggageEnabled={showCheckInBaggagePriceOW}
                    onwardFilterPtcType={flightDetails?.ptcFareType || ''}
                />
                <Modal
                    onClose={closeModal}
                    open={faresModalType?.show}
                    hideCrossIcon={false}
                    allowBackdropClose={true}
                    modalContentCss={{
                        width: 'fit-content',
                        minWidth: '956px',
                        overflow: 'visible',
                        position: 'relative'
                    }}
                >
                    <FareFamilyModal
                        cardDetails={cardDetails}
                        isRT={false}
                        tuppleIndex={faresModalType?.index || undefined} // type fix
                        showPerAdultPricing={showPerAdultPricing}
                        processedData={processedData}
                        isFFFlow={fareType === 'FF'}
                        closeModal={closeModal}
                        isSpecialRT={false}
                    />
                </Modal>
        </>
    )
}

export default TuppleRenderer
